import { useEffect } from "react";
import { Track, type LocalTrack, type Room } from "livekit-client";
import { feedbackBytesFrom, initialStall, nextStall } from "../lib/mediaStall";

const SAMPLE_MS = 1000;

/**
 * Replaces a call connection that looks alive but carries nothing (#2223 AC13).
 *
 * Samples once a second (see `lib/mediaStall.ts` for what counts as stalled).
 * On a stall it disconnects the room: both rejoin loops (signed-in and guest)
 * treat a client-side disconnect as involuntary and bring the person back
 * with a fresh connection, which is the path already proven for longer
 * losses. Fires at most once per mount; after it, the room is disconnected
 * and the stall state resets until the next connection.
 */
export function useMediaStallRecovery(room: Room): void {
  useEffect(() => {
    let state = initialStall();
    let stopped = false;
    let busy = false;

    const sample = async () => {
      if (stopped || busy) return;
      busy = true;
      try {
        const track = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track as
          | LocalTrack
          | undefined;
        const mst = track?.mediaStreamTrack;
        const micLive = !!mst && mst.readyState === "live" && !mst.muted;
        const report = micLive ? await track!.getRTCStatsReport().catch(() => undefined) : undefined;
        if (stopped) return;
        const r = nextStall(state, { state: room.state, micLive, feedbackBytes: feedbackBytesFrom(report) }, Date.now());
        state = r.state;
        if (r.stalled) {
          state = initialStall();
          void room.disconnect().catch(() => {});
        }
      } finally {
        busy = false;
      }
    };

    const id = setInterval(() => void sample(), SAMPLE_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [room]);
}
